import { useState } from 'react'
import { Loader2, Search, Plus } from 'lucide-react'
import { useAIStore } from '@/stores/aiStore'
import { useCompetitorMap } from '@/hooks/useCompetitorMap'
import type { BriefScope } from '@ispot/shared'
import { DEFAULT_VISIBLE_COLUMNS } from '@ispot/shared'
import { CompetitorTableView } from './CompetitorTableView'
import { CompetitorCategoryMap } from './CompetitorCategoryMap'
import { CompetitorAnalysisView } from './CompetitorAnalysisView'
import { CompetitorBriefPanel } from './CompetitorBriefPanel'
import { AddCompetitorModal } from './AddCompetitorModal'
import { SuggestionsInbox } from './SuggestionsInbox'
import { PositioningGapRadar } from './PositioningGapRadar'

type ViewMode = 'table' | 'category' | 'analysis' | 'gaps'

interface CompetitorMapPageProps {
  accountId: string
  projectId: string
}

export function CompetitorMapPage({ accountId, projectId }: CompetitorMapPageProps) {
  const [view, setView] = useState<ViewMode>('table')
  const [showAddModal, setShowAddModal] = useState(false)

  const apiKey = useAIStore(state => state.apiKey)

  const {
    competitors,
    columnSettings,
    activeBrief,
    briefScope,
    isLoading,
    isBriefLoading,
    isDiscovering,
    error,
    createCompetitor,
    updateCompetitor,
    deleteCompetitor,
    updateColumnSettings,
    openBrief,
    closeBrief,
    generateBrief,
    enrichCompetitor,
    enrichCompetitorBackground,
    discoverCompetitors,
  } = useCompetitorMap(accountId, projectId)

  const visibleColumns = columnSettings?.visibleColumns ?? (DEFAULT_VISIBLE_COLUMNS as Record<string, boolean>)

  const confirmedCompetitors = competitors.filter(c => !c.suggestedByAi)
  const suggestedCompetitors = competitors.filter(c => c.suggestedByAi)
  const existingCategories = Array.from(new Set(competitors.map(c => c.category).filter(Boolean))) as string[]

  async function handleAddCompetitor(data: Parameters<typeof createCompetitor>[0]) {
    // Pass apiKey so the server auto-enriches in the background
    return createCompetitor(data, apiKey ?? undefined)
  }

  async function handleConfirmSuggestion(id: string) {
    await updateCompetitor(id, { suggestedByAi: false })
    if (apiKey) enrichCompetitorBackground(id, apiKey)
  }

  async function handleEnrich(id: string) {
    if (!apiKey) return
    await enrichCompetitor(id, apiKey)
  }

  async function handleOpenBrief(scope: BriefScope) {
    await openBrief(scope)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'white' }}>
      {/* Page Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--color-text)', fontFamily: 'var(--font-display)', margin: 0 }}>
            Competitor Map
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
            {confirmedCompetitors.length} competitor{confirmedCompetitors.length !== 1 ? 's' : ''} tracked{suggestedCompetitors.length > 0 ? ` · ${suggestedCompetitors.length} suggested` : ''}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Action Buttons */}
        <button
          onClick={() => apiKey && discoverCompetitors(apiKey)}
          disabled={isDiscovering || !apiKey}
          aria-label="Discover competitors from product intelligence"
          style={{
            padding: '7px 14px', border: '1px solid var(--color-border)', borderRadius: '8px',
            fontSize: '13px', fontWeight: 500, cursor: isDiscovering || !apiKey ? 'not-allowed' : 'pointer',
            background: 'transparent', color: 'var(--color-text-secondary)',
            opacity: !apiKey ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '5px',
          }}
        >
          {isDiscovering ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={13} />}
          {isDiscovering ? 'Analyzing...' : 'Discover'}
        </button>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            padding: '7px 14px', background: 'var(--color-accent)', color: 'white',
            border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
          }}
        >
          <Plus size={13} /> Add
        </button>

        {/* View Toggle */}
        <div style={{
          display: 'flex',
          background: 'var(--color-warm-100)',
          borderRadius: '8px',
          padding: '3px',
          gap: '2px',
        }}>
          {([['table', 'Table'], ['category', 'Category Map'], ['analysis', 'Analysis'], ['gaps', 'Gap Radar']] as [ViewMode, string][]).map(([v, label]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                padding: '6px 14px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                background: view === v ? 'white' : 'transparent',
                color: view === v ? 'var(--color-text)' : 'var(--color-text-muted)',
                boxShadow: view === v ? '0 1px 3px rgba(29,26,21,0.08)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '12px 24px', background: '#fff3f3', borderBottom: '1px solid #ffcdd2', fontSize: '13px', color: '#dc3545' }}>
          {error}
        </div>
      )}

      {/* Body */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Main content */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Suggestions Inbox — shown above any view when suggestions exist */}
          {!isLoading && view !== 'gaps' && (
            <div style={{ padding: '12px 24px 0', flexShrink: 0 }}>
              <SuggestionsInbox
                suggestions={suggestedCompetitors}
                onConfirm={handleConfirmSuggestion}
                onDismiss={deleteCompetitor}
              />
            </div>
          )}

          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
              Loading…
            </div>
          ) : view === 'table' ? (
            <CompetitorTableView
              competitors={confirmedCompetitors}
              visibleColumns={visibleColumns}
              onUpdateColumns={updateColumnSettings}
              onUpdate={updateCompetitor}
              onDelete={deleteCompetitor}
              onOpenBrief={handleOpenBrief}
              onAddClick={() => setShowAddModal(true)}
              onEnrich={apiKey ? handleEnrich : undefined}
            />
          ) : view === 'category' ? (
            <CompetitorCategoryMap
              competitors={confirmedCompetitors}
              onOpenBrief={handleOpenBrief}
              onAddClick={() => setShowAddModal(true)}
            />
          ) : view === 'analysis' ? (
            <CompetitorAnalysisView
              competitors={confirmedCompetitors}
              briefScope={briefScope}
              activeBrief={activeBrief}
              isBriefLoading={isBriefLoading}
              onOpenBrief={handleOpenBrief}
              onCloseBrief={closeBrief}
              onGenerateBrief={generateBrief}
              onEnrich={apiKey ? enrichCompetitor : undefined}
              onAddClick={() => setShowAddModal(true)}
            />
          ) : (
            <PositioningGapRadar accountId={accountId} projectId={projectId} />
          )}
        </div>

        {/* Brief Panel — suppressed in analysis view where briefs expand inline */}
        {briefScope && view !== 'analysis' && (
          <CompetitorBriefPanel
            brief={activeBrief}
            scope={briefScope}
            competitors={competitors}
            isLoading={isBriefLoading}
            onClose={closeBrief}
            onGenerate={generateBrief}
            onEnrich={enrichCompetitor}
          />
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <AddCompetitorModal
          accountId={accountId}
          projectId={projectId}
          onAdd={handleAddCompetitor}
          onClose={() => setShowAddModal(false)}
          existingCategories={existingCategories}
        />
      )}
    </div>
  )
}
